import {
  Autocomplete,
  Button,
  Divider,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { resources } from '../../core/services/resources';
import type { TenantRole, User } from '../../core/types/data-models';

const ASSIGNABLE_ROLES: TenantRole[] = ['owner', 'admin', 'member'];

interface TenantUsersSectionProps {
  tenantId: number;
}

/**
 * Membership list + add-user form for a tenant, embedded directly in the
 * Edit Tenant dialog. Attach/detach/role changes take effect immediately
 * (via the junction endpoint), independent of the dialog's own Save button.
 */
export function TenantUsersSection({ tenantId }: TenantUsersSectionProps) {
  const qc = useQueryClient();
  const tenantUsersService = resources.tenantUsersFor(tenantId);
  const queryKey = ['tenants', tenantId, 'users'];

  const attachedQuery = useQuery({
    queryKey,
    queryFn: () => tenantUsersService.list({ pageSize: 100, include: ['user'] }),
  });

  const catalogQuery = useQuery({
    queryKey: ['users', 'select'],
    queryFn: () => resources.users.list({ pageSize: 200, sort: 'email' }),
  });

  const invalidate = () => qc.invalidateQueries({ queryKey });

  const attachUser = useMutation({
    mutationFn: ({ userId, role }: { userId: number; role: TenantRole }) =>
      tenantUsersService.attach(userId, { role }),
    onSuccess: invalidate,
  });
  const updateRole = useMutation({
    mutationFn: ({ userId, role }: { userId: number; role: TenantRole }) =>
      tenantUsersService.update(userId, { role }),
    onSuccess: invalidate,
  });
  const detachUser = useMutation({
    mutationFn: (userId: number) => tenantUsersService.detach(userId),
    onSuccess: invalidate,
  });

  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [selectedRole, setSelectedRole] = useState<TenantRole>('member');

  const attached = attachedQuery.data?.data ?? [];
  const attachedUserIds = new Set(attached.map((row) => row.userId));
  const candidates = (catalogQuery.data?.data ?? []).filter((u) => !attachedUserIds.has(u.id));

  const userLabel = (u: User) => (u.name ? `${u.name} (${u.email})` : u.email);

  const handleAdd = () => {
    if (!selectedUser) return;
    attachUser.mutate({ userId: selectedUser.id, role: selectedRole });
    setSelectedUser(null);
    setSelectedRole('member');
  };

  return (
    <Stack spacing={1.5}>
      <Divider />
      <Typography variant="subtitle2" fontWeight={600}>
        Tenant users
      </Typography>

      <Stack direction="row" spacing={1} alignItems="flex-start">
        <Autocomplete<User, false, false, false>
          options={candidates}
          getOptionLabel={userLabel}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          value={selectedUser}
          onChange={(_, next) => setSelectedUser(next)}
          renderInput={(params) => <TextField {...params} label="Add a user" size="small" />}
          sx={{ flex: 1 }}
        />
        <TextField
          select
          label="Role"
          size="small"
          value={selectedRole}
          onChange={(e) => setSelectedRole(e.target.value as TenantRole)}
          sx={{ width: 140 }}
        >
          {ASSIGNABLE_ROLES.map((role) => (
            <MenuItem key={role} value={role}>
              {role}
            </MenuItem>
          ))}
        </TextField>
        <Button
          variant="outlined"
          disabled={!selectedUser || attachUser.isPending}
          onClick={handleAdd}
          sx={{ height: 40 }}
        >
          Add
        </Button>
      </Stack>

      <Stack spacing={1}>
        {attached.map((row) => (
          <Stack key={row.id} direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" sx={{ flex: 1 }} noWrap>
              {row.user ? userLabel(row.user) : `User #${row.userId}`}
            </Typography>
            <TextField
              select
              size="small"
              value={row.role}
              onChange={(e) =>
                updateRole.mutate({ userId: row.userId, role: e.target.value as TenantRole })
              }
              sx={{ width: 140 }}
            >
              {ASSIGNABLE_ROLES.map((role) => (
                <MenuItem key={role} value={role}>
                  {role}
                </MenuItem>
              ))}
              {!ASSIGNABLE_ROLES.includes(row.role) && (
                <MenuItem value={row.role}>{row.role}</MenuItem>
              )}
            </TextField>
            <IconButton
              size="small"
              onClick={() => detachUser.mutate(row.userId)}
              aria-label="remove user from tenant"
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Stack>
        ))}
        {attached.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No users assigned to this tenant yet.
          </Typography>
        )}
      </Stack>
    </Stack>
  );
}
